import { inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { EnvironmentLoader } from '@environments/environment-loader';
import { environment } from '@environments/environment';
import { Environment } from '@environments/environment.interface';
import { ConfigStore, FeatureFlags, RuntimeEnvironmentConfig } from '@stores/config.store';
import { LoggerService } from '@core/services/logger.service';

/**
 * Validation result from ConfigStore
 */
interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Extended ConfigStore interface with runtime methods
 */
interface ConfigStoreWithRuntime {
  updateRuntimeConfig(env: RuntimeEnvironmentConfig): void;
  validateConfiguration?(): ValidationResult;
  features(): Partial<FeatureFlags>;
}

/** Global window extension for feature flags */
declare global {
  interface Window {
    __RAZOR_FEATURES__?: Partial<FeatureFlags> & {
      enableMLFeatures?: boolean;
      enableBetaFeatures?: boolean;
    };
  }
}

/**
 * Environment Bootstrap Initializer
 * 
 * Loads runtime environment configuration before the application starts.
 * This ensures that all stores and services have access to the complete
 * runtime configuration including server-provided settings.
 * 
 * Features:
 * - Loads runtime environment variables
 * - Merges with build-time configuration
 * - Updates ConfigStore with runtime values
 * - Handles fallback to build-time config on failure
 */
export function initializeEnvironment(): () => Promise<void> {
  return async (): Promise<void> => {
    const http = inject(HttpClient);
    const configStore = inject(ConfigStore) as unknown as ConfigStoreWithRuntime;
    const logger = inject(LoggerService);
    const loader = new EnvironmentLoader(http);

    try {
      logger.debug('Initializing runtime environment...', { module: 'EnvironmentInitializer' });

      // Try to load cached environment first for faster startup
      // Only use cache if it matches the expected environment mode (dev/prod)
      const cachedEnv = loader.loadCachedEnvironment(environment.production) as RuntimeEnvironmentConfig | null;
      if (cachedEnv) {
        logger.debug('Using cached environment configuration', { module: 'EnvironmentInitializer' });
        configStore.updateRuntimeConfig(cachedEnv);
        return;
      }

      // Load fresh environment configuration
      logger.debug('Loading fresh environment configuration...', { module: 'EnvironmentInitializer' });
      const runtimeEnv = await firstValueFrom(loader.loadEnvironment(environment as Environment)) as RuntimeEnvironmentConfig | undefined;

      if (!runtimeEnv) {
        throw new Error('Failed to load runtime environment');
      }

      // Update ConfigStore with runtime configuration
      configStore.updateRuntimeConfig(runtimeEnv);

      logger.debug('Runtime environment initialized successfully', { module: 'EnvironmentInitializer' });
      // Log details defensively to avoid type issues
      try {
        logger.debug('Environment details', {
          module: 'EnvironmentInitializer',
          production: runtimeEnv.production,
          apiUrl: runtimeEnv.api?.baseUrl ?? runtimeEnv.apiUrl,
          features: Object.keys(runtimeEnv.features ?? {}).length,
          buildTime: runtimeEnv.buildTime
        });
      } catch {
        // Ignore logging failures to avoid blocking startup.
      }

    } catch (error) {
      logger.error('Failed to initialize runtime environment', { module: 'EnvironmentInitializer' }, error);
      logger.warn('Falling back to build-time configuration', { module: 'EnvironmentInitializer' });

      // Fallback to build-time environment
      const fallbackEnv: RuntimeEnvironmentConfig = {
        production: environment.production,
        apiUrl: environment.apiUrl,
        features: environment.features
      };
      configStore.updateRuntimeConfig(fallbackEnv);
    }
  };
}

/**
 * Environment Validation Initializer
 * 
 * Validates the loaded environment configuration to ensure all required
 * settings are present and valid before the application continues.
 */
export function validateEnvironment(): () => Promise<void> {
  return (): Promise<void> => {
    const configStore = inject(ConfigStore) as unknown as ConfigStoreWithRuntime;
    const logger = inject(LoggerService);

    try {
      logger.debug('Validating environment configuration...', { module: 'EnvironmentInitializer' });

      const validation: ValidationResult = configStore.validateConfiguration?.() ?? { valid: true, errors: [], warnings: [] };

      if (!validation.valid) {
        logger.error('Environment validation failed', { module: 'EnvironmentInitializer', errors: validation.errors });
        throw new Error('Invalid environment configuration');
      }

      if (validation.warnings.length > 0) {
        validation.warnings.forEach((warning: string) =>
          logger.warn('Environment warning', { module: 'EnvironmentInitializer', warning })
        );
      }

      logger.debug('Environment configuration validated successfully', { module: 'EnvironmentInitializer' });

    } catch (error) {
      logger.error('Environment validation failed', { module: 'EnvironmentInitializer' }, error);
      // Don't throw here - let the app start with warnings
      logger.warn('Application starting with invalid configuration', { module: 'EnvironmentInitializer' });
    }
    return Promise.resolve();
  };
}

/**
 * Feature Flag Initializer
 * 
 * Initializes feature flags based on the runtime environment configuration.
 * This allows for dynamic feature toggling based on environment and user context.
 */
export function initializeFeatureFlags(): () => Promise<void> {
  return (): Promise<void> => {
    const configStore = inject(ConfigStore) as unknown as ConfigStoreWithRuntime;
    const logger = inject(LoggerService);

    try {
      logger.debug('Initializing feature flags...', { module: 'EnvironmentInitializer' });

      const features = configStore.features();
      const enabledFeatures = Object.entries(features ?? {})
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);

      logger.debug('Enabled features', { module: 'EnvironmentInitializer', features: enabledFeatures });

      // Store feature flags in global context for easy access.
      // GAP-C18: defineProperty with writable:false prevents both mutation and reassignment
      // via browser console. Object.freeze prevents property mutation on the value itself.
      const doc = inject(DOCUMENT);
      const win = doc.defaultView as (Window & typeof globalThis & { __RAZOR_FEATURES__?: Partial<FeatureFlags> }) | null;
      if (win) {
        Object.defineProperty(win, '__RAZOR_FEATURES__', {
          value: Object.freeze({ ...features }),
          writable: false,
          configurable: false,
          enumerable: true
        });
      }

      logger.debug('Feature flags initialized successfully', { module: 'EnvironmentInitializer' });

    } catch (error) {
      logger.error('Failed to initialize feature flags', { module: 'EnvironmentInitializer' }, error);
      // Set default features on failure
      const doc = inject(DOCUMENT);
      const win = doc.defaultView as (Window & typeof globalThis & { __RAZOR_FEATURES__?: Partial<FeatureFlags> }) | null;
      if (!win) {
        return Promise.resolve();
      }
      Object.defineProperty(win, '__RAZOR_FEATURES__', {
        value: Object.freeze({
          enableAdvancedAnalytics: false,
          enableMLFeatures: false,
          enableBetaFeatures: false,
          enableDebugMode: !environment.production
        }),
        writable: false,
        configurable: false,
        enumerable: true
      });
    }
    return Promise.resolve();
  };
}
